'use client'

import { useState } from 'react'
import { Check } from 'lucide-react'
import { ROUTES } from '@/config'
import { cn } from '@/lib/utils'
import { PLANS } from '@/config/pricing'
import { ButtonLink } from './primitives'

const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    audience: 'Für private Eigentümer mit kleinen Portfolios.',
    features: ['1 Benutzer', 'bis 20 Einheiten', 'alle Funktionen'],
    cta: 'Starter wählen',
    variant: 'secondary',
  },
  {
    key: 'professional',
    name: 'Professional',
    audience: 'Für wachsende Verwaltungsbüros und Profis.',
    features: ['3 Benutzer', 'bis 150 Einheiten', 'alle Funktionen'],
    cta: 'Professional wählen',
    variant: 'primary',
    highlight: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    audience: 'Für grosse Verwaltungen, Fonds und Genossenschaften.',
    features: ['Benutzer und Einheiten nach Absprache', 'alle Funktionen'],
    cta: 'Kontakt aufnehmen',
    variant: 'secondary',
    href: '/kontakt',
  },
]

const INTERVALS = [
  { key: 'monthly', label: 'Monatlich' },
  { key: 'yearly', label: 'Jährlich · 1 Monat gratis' },
]

// Fixed block height keeps the feature lists of all three columns on one line.
function Price({ plan, yearly }) {
  const value = plan && (yearly ? plan.yearly : plan.monthly)
  return (
    <div className="flex min-h-[4.25rem] flex-col gap-1.5">
      <p className="flex items-baseline gap-2 tabular-nums">
        <span className="text-[34px] font-[450] leading-none text-ink [font-stretch:110%]">
          {plan ? `CHF ${value.toLocaleString('de-CH')}` : 'Auf Anfrage'}
        </span>
        {plan && (
          <span className="text-sm text-graphite">
            / {yearly ? 'Jahr' : 'Monat'}
          </span>
        )}
      </p>
      <p className="font-mono text-xs leading-relaxed text-graphite">
        {!plan && 'Individuelles Angebot'}
        {plan &&
          yearly &&
          `CHF ${Math.round(plan.yearly / 12)} pro Monat · zzgl. MwSt.`}
        {plan && !yearly && 'zzgl. MwSt.'}
      </p>
    </div>
  )
}

export function HomePricing() {
  const [billing, setBilling] = useState('monthly')
  const yearly = billing === 'yearly'

  return (
    <div className="flex flex-col gap-6">
      <div
        role="group"
        aria-label="Abrechnungsintervall"
        className="inline-flex self-start rounded-control border border-rule-strong bg-surface p-0.5"
      >
        {INTERVALS.map(option => {
          const active = option.key === billing
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={active}
              onClick={() => setBilling(option.key)}
              className={cn(
                'h-8 rounded-[3px] px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal',
                active ? 'bg-ink text-white' : 'text-graphite hover:text-ink'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      <div className="grid overflow-hidden rounded-card border border-rule-strong bg-surface md:grid-cols-3">
        {TIERS.map((tier, i) => {
          const plan = PLANS[tier.key]
          const href =
            tier.href ?? `${ROUTES.REGISTER}?plan=${tier.key}_${billing}`
          return (
            <div
              key={tier.key}
              className={cn(
                'flex flex-col gap-6 p-6 lg:p-8',
                i > 0 && 'border-t border-rule md:border-l md:border-t-0',
                tier.highlight && 'bg-mist/60'
              )}
            >
              <div className="flex flex-col gap-1.5">
                <h3 className="text-lg font-semibold text-ink">{tier.name}</h3>
                <p className="text-[15px] leading-snug text-graphite">
                  {tier.audience}
                </p>
              </div>
              <Price plan={plan} yearly={yearly} />
              <ul className="flex flex-1 flex-col gap-2.5 border-t border-rule pt-5 text-[15px] text-ink">
                {tier.features.map(feature => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <Check
                      aria-hidden
                      className="mt-0.5 h-4 w-4 shrink-0 text-signal"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
              <ButtonLink href={href} variant={tier.variant} className="w-full">
                {tier.cta}
              </ButtonLink>
            </div>
          )
        })}
      </div>

      <p className="font-mono text-xs leading-relaxed text-graphite">
        Jahresplan: 12 Monate zum Preis von 11 · 30 Tage Geld-zurück-Garantie
        auf Jahrespläne · Jederzeit kündbar
      </p>
    </div>
  )
}
